// home.styles.ts

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { keyframes } from 'styled-components';

// --- Theme Object (Enhanced Colors & Typography) ---
export const theme = {
  colors: {
    primary: '#FF9900',
    primaryLight: '#FFEDCC',
    primaryDark: '#E68A00',
    secondary: '#232F3E',
    secondaryLight: '#EBF2F9',
    accent: '#007185',
    dark: '#111827',
    text: '#333333',
    textLight: '#555555',
    textMuted: '#999999',
    white: '#FFFFFF',
    // ADJUSTMENT 1: Changed background color to orange
    background: 'orange', 
    border: '#D5D9D9',
    borderLight: '#E7E7E7',
    danger: '#CC0C39',
    dangerLight: '#FAE7EA',
    success: '#4CAF50',
    successLight: '#E8F5E9',
    lightback: '#ADD8E6',
    black: '#000000',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  breakpoints: {
    sm: '600px',
    md: '960px',
    lg: '1280px',
    xl: '1440px',
  },
  typography: {
    fontFamily: "'Amazon Ember', Arial, sans-serif",
    headerFontFamily: "'Amazon Ember Display', Arial, sans-serif",
    lineHeight: 1.6,
    bodyFontSize: '1rem',
    smallFontSize: '0.875rem',
    largeFontSize: '1.125rem',
  }
};

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const loadingAnimation = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const flipIn = keyframes`
  0% {
    transform: rotateY(-180deg) scale(0.5);
    opacity: 0;
  }
  100% {
    transform: rotateY(0deg) scale(1);
    opacity: 1;
  }
`;

// ──────────────────────────────────────────────────────────────
// 1. LISTINGS GRID – 2 cards per row (standard + real-estate)
// ──────────────────────────────────────────────────────────────
export const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1200px;          /* Much wider – allows 3+ columns if screen is big */
  margin: 20px auto 0;
  padding: 0 16px;            /* Optional: safe side padding */
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 10px;
  }
`;

// ──────────────────────────────────────────────────────────────
// 2. PREMIUM STALLS GRID – 1 card per row (full width)
// ──────────────────────────────────────────────────────────────
export const PremiumStallsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;               /* always 1 column */
  gap: 20px;
  width: 100%;
  max-width: 760px;                         /* same as ListingsGrid */
  margin: 20px auto;
  padding: 20px 12px;
  perspective: 1000px;
  justify-content: center;

  /* -------------------------------------------------
     TABLET + DESKTOP – keep centered, max 720px wide
     ------------------------------------------------- */
  @media (min-width: 601px) {
    grid-template-columns: minmax(340px, 720px);
  }

  @media (max-width: 600px) {
    padding: 10px;
    max-width: 100%;
    /* Prevent any child from being wider than the screen */
    > * {
      max-width: calc(100vw - 20px);        /* 10px left + 10px right */
      width: 100% !important;
      box-sizing: border-box;
    }
  }
`;

// ──────────────────────────────────────────────────────────────
// 3. SKELETON GRID – Match ListingsGrid (2 per row)
// ──────────────────────────────────────────────────────────────
export const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 760px;
  margin: 20px auto 0;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    padding: 0 10px;
  }
`;

// --- ADJUSTMENT 1: New Global Wrapper for Background ---
export const HomeWrapper = styled.div`
  justify-self:center;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.lightback}; 
`;

// --- Layout Components ---
export const SidebarHeading = styled.h4`
  margin: 0 0 10px;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.dark};
`;

export const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 300px;
  max-width: 800px;
  margin: 0 auto;
  order: 2;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: none;
  }
`;

export const SidebarContainer = styled.aside<{ className?: string }>`
  width: 280px;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeIn} 0.5s ease-in-out;

  @media (max-width: 1024px) {
    width: 100%;
    margin-bottom: 20px;
    transition: max-height 0.5s ease, opacity 0.5s ease;
    max-height: 60vh;
    opacity: 1;
    overflow-y: auto;
    overflow-x: hidden;

    &.hidden {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
      margin-bottom: 0;
      overflow: hidden;
    }
  }
`;

export const MarketplaceSidebarWrapper = styled.div<{ $isOpen: boolean }>`
  order: 1;
  
  @media (max-width: 1024px) {
    position: fixed; /* Positions the element relative to the viewport */
    top: 100px; /* Adjust this value to place it below your search bar/header */
    left: 0;
    width: 100%;
    height: calc(100vh - 100px); /* Adjust height to fill the remaining screen */
    background-color: ${({ theme }) => theme.colors.background};
    z-index: 99; /* Ensures it's above other content */
    display: ${props => props.$isOpen ? 'block' : 'none'};
    animation: ${fadeIn} 0.5s ease-in-out;
  }
  
  @media (min-width: 1025px) {
    display: block;
    margin-right: 20px;
  }
`;

export const RealEstateSidebarWrapper = styled.div<{ $isOpen: boolean }>`
  order: 3;
  
  @media (max-width: 1024px) {
    position: fixed; /* Positions the element relative to the viewport */
    top: 100px; /* Aligns with the other sidebar */
    left: 0;
    width: 100%;
    height: calc(100vh - 100px);
    background-color: ${({ theme }) => theme.colors.background};
    z-index: 99;
    display: ${props => props.$isOpen ? 'block' : 'none'};
    animation: ${fadeIn} 0.5s ease-in-out;
  }
  
  @media (min-width: 1025px) {
    display: block;
    margin-left: 20px;
  }
`;

export const RealEstateSidebar = styled(SidebarContainer)`
  // Additional specific styles if needed
`;

export const SidebarToggleContainer = styled.div`
  display: none;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 0 10px;
  
  @media (max-width: 1024px) {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${({ theme }) => theme.colors.background}; /* Uses the new background color */
    padding: 10px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

export const MainLayout = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 10px;
    gap: 0;
  }
`;

export const SidebarToggle = styled.button<{ $isActive?: boolean }>`
  background-color: ${({ theme, $isActive }) => 
    $isActive ? theme.colors.primaryDark : theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 8px;
  padding: 12px 15px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  flex: 1;
  margin: 0 5px;
  
  &:hover {
    background-color: ${({ theme, $isActive }) => 
      $isActive ? theme.colors.primaryDark : theme.colors.primaryLight};
    color: ${({ theme, $isActive }) => 
      $isActive ? theme.colors.white : theme.colors.primaryDark};
  }
`;
export const CategoryButton = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  text-align: left;
  background-color: ${props => props.$isActive ? props.theme.colors.primaryLight : 'transparent'};
  color: ${props => props.$isActive ? props.theme.colors.primaryDark : props.theme.colors.text};
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
   background-color: ${props => props.$isActive ? props.theme.colors.primaryLight : props.theme.colors.secondaryLight};
    color: ${props => props.theme.colors.primaryDark};
  }
`;

// --- Category Components ---
interface CategoryIconProps {
  $isActive: boolean;
}

export const CategoryIcon = styled(FontAwesomeIcon)<CategoryIconProps>`
  color: ${({ $isActive, theme }) => $isActive ? theme.colors.primaryDark : theme.colors.accent};
  transition: color 0.2s ease;
  min-width: 20px;
`;

export const CategoryButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const CategoryNameContainer = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// --- ADJUSTMENT 2: Search Bar Layout Components ---

// NEW: Unified wrapper for both search inputs
export const UnifiedSearchBarWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 15px 20px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 1400px;
  margin: 20px auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 5px;
    padding: 10px;
    margin: 10px auto;
    width: 95%;
  }
`;

// SearchBarContainer (for Global Search) - Modified to take up 2/3 of the space
export const SearchBarContainer = styled.div`
  position: relative;
  flex: 2; 
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
    width: 100%;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textMuted};
`;

// LocationSelectContainer (for Location Search) - Modified to take up 1/3 of the space
export const LocationSelectContainer = styled.div`
  position: relative;
  flex: 1; 
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const LocationIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

// --- Misc. Components ---
export const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 1.2em;
  color: ${({ theme }) => theme.colors.primaryDark};
  background-color: ${({ theme }) => theme.colors.primaryLight};
  border-radius: 8px;
  margin: 20px auto;
  max-width: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
`;

export const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 1.2em;
  color: ${({ theme }) => theme.colors.danger};
  background-color: ${({ theme }) => theme.colors.dangerLight};
  border: 1px solid ${({ theme }) => theme.colors.danger};
  border-radius: 8px;
  margin: 20px auto;
  max-width: 600px;
`;

export const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid ${({ theme }) => theme.colors.primaryDark}; /* Changed spinner color to primary dark */
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: ${spin} 1s linear infinite;
`;

export const NoPostsMessage = styled.p`
  text-align: center;
  color: #666;
  font-size: 1.1em;
  margin-top: 30px;
  padding: 20px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
`;

export const CenteredHeading = styled.h3`
  text-align: center;
`;

export const ZoomOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

export const ZoomedImageContainer = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ZoomedImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  display: block;
  object-fit: contain;
`;

export const SoldBadge = styled.div`
  background-color: ${({ theme }) => theme.colors.danger};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 5px;
  font-size: 0.9rem;
  font-weight: bold;
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing.sm};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  align-self: flex-end;
`;

// --- Location Dropdown Components ---
export const LocationDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 8px 8px;
`;

export const LocationOption = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

export const PremiumStallCard = styled.div`
  animation: ${flipIn} 1.2s ease-in-out forwards;
  position: relative;
  transform-style: preserve-3d;
`;

// --- Floating Buttons ---
export const FloatingButtonsContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 999;

  @media (max-width: 768px) {
    flex-direction: row;
    left: 20px;
    right: 20px;
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    bottom: 10px;
    left: 10px;
    right: 10px;
    gap: 5px;
  }
`;

export const FloatingButton = styled.button`
  background: linear-gradient(145deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  /* Initial entrance animation + continuous shake */
  animation: slideInRight 0.5s ease-out both, 
             attentionShake 2s ease-in-out 0.5s infinite;

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes attentionShake {
    0%, 100% {
      transform: translateX(0) rotate(0deg);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    10%, 30%, 50%, 70%, 90% {
      transform: translateX(-2px) rotate(-1deg);
      box-shadow: 0 6px 12px rgba(255, 193, 7, 0.4);
    }
    20%, 40%, 60%, 80% {
      transform: translateX(2px) rotate(1deg);
      box-shadow: 0 6px 12px rgba(255, 193, 7, 0.4);
    }
  }

  /* Stagger delays for entrance and shake */
  &:nth-child(1) { 
    animation-delay: 0.1s, 0.6s; 
  }
  &:nth-child(2) { 
    animation-delay: 0.2s, 0.7s; 
  }
  &:nth-child(3) { 
    animation-delay: 0.3s, 0.8s; 
  }

  /* Hover effects - stop shake and enhance */
  &:hover {
    background: linear-gradient(145deg, #45a049, #4CAF50);
    transform: translateY(-3px) scale(1.05) !important;
    box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4) !important;
    animation: none; /* Stop all animations on hover */
  }

  &:active {
    transform: translateY(-1px) scale(1.02) !important;
    transition: all 0.1s ease;
  }

  @media (max-width: 768px) {
    padding: 10px 18px;
    font-size: 14px;
    border-radius: 30px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    border-radius: 25px;
  }
`;

// --- Skeleton Loader Components (Removed unused ones, kept used ones) ---
export const SkeletonCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const SkeletonImage = styled.div`
  width: 100%;
  height: 120px;
  background-color: ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

export const SkeletonText = styled.div<{ width?: string }>`
  height: 16px;
  background-color: ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
  width: ${({ width }) => width || '100%'};
  margin-bottom: 8px;
`;

export const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 10px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;
export const WelcomePopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

export const WelcomePopupContent = styled.div`
  background: #fff;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  padding: 1.5rem 1.25rem;
  position: relative;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

export const WelcomeCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #888;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
`;

export const WelcomeTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0 0 0.5rem;
  color: #1a202c;
`;

export const WelcomeMessage = styled.p`
  font-size: 0.95rem;
  line-height: 1.4;
  color: #4a5568;
  margin-bottom: 1.5rem;
`;

export const WelcomeButton = styled.button`
  background: #2b6cb0;
  color: white;
  border: none;
  padding: 0.75rem 1.75rem;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2c5282;
  }
`;